import React, { PureComponent } from 'react';
import { HotspotDto, HotspotStatus, TimePeriod } from 'app/model/CORE';
import { Flex } from 'app/components/common/Flex';
import { Button, Select } from 'antd';
import { PlusOutlined, SettingFilled, TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import { ShowModalEvent } from 'app/common/eventbus/Events';
import { HotspotPanel } from 'app/components/panels/measure/HotspotPanel';
import { FlowGrid } from 'app/components/dashboard/network/flows/FlowGrid';
import Utils from 'app/common/util/Utils';
import { HotspotLoad } from 'app/components/dashboard/network/flows/HotspotLoad';
import { ExtendedMonitoredTv, MonitoredEvent, MonitoredMeasure } from 'app/model/SOL49';
import { SelectionLabel } from 'app/components/common/SelectionLabel';
import './Hotspots.less';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import {
    HotspotSettings,
    HotspotSettingsContent,
    HotspotSettingsTitle,
} from 'app/components/dashboard/detailed/hotspots/HotspotSettings';
import memoizeOne from 'memoize-one';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';

interface HotspotsProps {
    hotspots: { [key: string]: HotspotDto[] };
    selection: string[];

    onSelectionChange(selection: string[]): void;

    monitoredTvList: ExtendedMonitoredTv[];
    monitoredEvents?: MonitoredEvent[];
    measures: MonitoredMeasure[];
    currentTime: Date;
    monitoredPeriod: TimePeriod;
    settings: HotspotSettings;
    onHotspotSettingsChange(settings: HotspotSettings): void;
    textFilter: string;
}

interface HotspotsState {
    gridDisplay: boolean;
    sortBy: 'name' | 'duration' | 'start_time' | 'end_time';
}

export class Hotspots extends PureComponent<HotspotsProps, HotspotsState> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    constructor(props) {
        super(props);
        this.state = {
            gridDisplay: true,
            sortBy: 'name',
        };
    }

    handleSettingsChange = (settings: HotspotSettings) => {
        this.props.onHotspotSettingsChange(settings);
        this.settingsPopoverRef.current.hide();
    };

    getFilteredHotspots = memoizeOne(
        (
            hotspots: {
                [key: string]: HotspotDto[];
            },
            textFilter: string,
            monitoredPeriod: TimePeriod,
            settings: HotspotSettings,
        ) => {
            if (!hotspots) return;

            return Object.fromEntries(
                Object.entries(hotspots).filter(([tvId, v]) => {
                    if (textFilter) {
                        const upperCasetextFilter = Utils.toUpperCase(textFilter).split(' ');
                        //if the query text has a * uses the simpleWildcardEquals, otherwise checks if a tv or measure contains the given query text
                        const matchingTextFilters = upperCasetextFilter.filter(textFilterEl => {
                            const re = Utils.wildcardToRegex(textFilterEl);
                            return re.test(tvId);
                        });
                        if (matchingTextFilters.length <= 0) return false;
                    }

                    if (settings) {
                        if (settings.activeOnly) {
                            const isActive = v.some(
                                hotspot =>
                                    hotspot.status === HotspotStatus.ACTIVE ||
                                    hotspot.status === HotspotStatus.DRAFT ||
                                    hotspot.status === HotspotStatus.ACCEPTABLE,
                            );
                            if (!isActive) return false;
                        }

                        if (settings.monitoredPeriodOnly) {
                            const inMonitoredPeriod = v.some(hotspot =>
                                TimePeriodUtils.intersects(hotspot.hotspotId.timePeriod, monitoredPeriod),
                            );
                            if (!inMonitoredPeriod) return false;
                        }
                    }

                    return true;
                }),
            );
        },
    );

    render() {
        const filteredHotspots = this.getFilteredHotspots(
            this.props.hotspots,
            this.props.textFilter,
            this.props.monitoredPeriod,
            this.props.settings,
        );
        const tvs = (this.props.hotspots && Object.keys(this.props.hotspots)) || [];
        const nbHotspots = tvs.length;
        const nbSelected = this.props.selection?.length || 0;
        const selectionLabelVisible = nbSelected > 0;

        return (
            <Flex direction="vertical" className="hotspots overflow detailed-subpanel">
                <Flex className="detailed-subpanel-header" size="2em">
                    <Flex className="detailed-subpanel-header-left" size="50%">
                        <SelectionLabel
                            visible={selectionLabelVisible}
                            title={`${nbSelected} hotspot${nbSelected > 1 ? 's' : ''} selected`}
                            subTitle={`${nbHotspots} in total`}
                            onSelectAll={this.handleSelectAll(Object.keys(filteredHotspots))}
                            onClearSelection={this.handleClearSelection}
                            children={this.renderHotspotSelectionInfo(nbHotspots)}
                        />
                        <Flex className="new-hotspot">
                            <Button icon={<PlusOutlined />} onClick={this.handleNewHotspotClick}>
                                NEW HOTSPOT
                            </Button>
                        </Flex>
                    </Flex>
                    <Flex className="detailed-subpanel-header-right" size="50%">
                        <ConditionalRender condition={this.state.gridDisplay}>
                            <Select
                                className="hotspot-sortby"
                                value={this.state.sortBy}
                                onChange={value => this.setState({ sortBy: value })}>
                                <Select.Option value="name">Name</Select.Option>
                                <Select.Option value="duration">Duration</Select.Option>
                                <Select.Option value="start_time">Start Time</Select.Option>
                                <Select.Option value="end_time">End Time</Select.Option>
                            </Select>
                        </ConditionalRender>
                        <span className="detailed-subpanel-header-right" style={{ display: 'flex' }}>
                            <ConditionalRender condition={this.state.gridDisplay}>
                                <UnorderedListOutlined /> {/* disabled for now  onClick={this.switchDisplayMode}/> */}
                            </ConditionalRender>
                            <ConditionalRender condition={!this.state.gridDisplay}>
                                <TableOutlined onClick={this.switchDisplayMode} />
                            </ConditionalRender>
                            <ClosablePopover
                                key="settings-action"
                                ref={this.settingsPopoverRef}
                                trigger="click"
                                placement="bottom"
                                title={<HotspotSettingsTitle />}
                                content={
                                    <HotspotSettingsContent
                                        settings={this.props.settings}
                                        onChange={this.handleSettingsChange}
                                        onClose={() => this.settingsPopoverRef.current.hide()}
                                    />
                                }>
                                <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                            </ClosablePopover>
                        </span>
                    </Flex>
                </Flex>
                <ConditionalRender condition={this.state.gridDisplay}>
                    {this.renderHotspotsGrid(filteredHotspots)}
                </ConditionalRender>
                {/*<ConditionalRender condition={!this.state.gridDisplay}>{this.renderMeasuresTable(filteredMeasures)}</ConditionalRender>*/}
            </Flex>
        );
    }

    renderHotspotsGrid = (hotspots: { [key: string]: HotspotDto[] }) => {
        return (
            <Flex className="content overflow" grow={false}>
                <FlowGrid columns={4}>
                    {Object.entries(hotspots).map(([tv, hotspots]) => {
                        const selected = this.props.selection.includes(tv);
                        const monitoredTv = this.props.monitoredTvList.find(mtv => mtv.refLocation.id === tv);
                        const monitoredEvents = this.props.monitoredEvents?.filter(
                            event =>
                                (MonitoredEventUtils.isNoteEvent(event) && event.tvs?.includes(tv)) ||
                                (MonitoredEventUtils.isWeatherEvent(event) &&
                                    event.refLocs?.some(refLoc => Utils.isEquals(refLoc, monitoredTv?.tvRefLocation))),
                        );
                        const measures = this.props.measures?.filter(
                            m => MeasureUtils.getTvLocation(m.measure).id === tv,
                        );
                        return (
                            <HotspotLoad
                                key={`hotspot-load-${tv}`}
                                tv={tv}
                                hotspots={hotspots}
                                selected={selected}
                                onSelectionChange={selected => this.handleSelectionUpdate(tv, selected)}
                                currentTime={this.props.currentTime}
                                monitoredTv={monitoredTv}
                                measures={measures}
                                monitoredEvents={monitoredEvents}
                            />
                        );
                    })}
                </FlowGrid>
            </Flex>
        );
    };

    renderHotspotSelectionInfo = nbHotspots => (
        <div className={'hotspot-selection-info'}>
            <span className="detailed-subpanel-title">HOTSPOTS</span>
            <span className="detailed-subpanel-title-info">{nbHotspots}</span>
        </div>
    );

    handleSelectAll = (selection: string[]) => {
        const newSelection = Utils.copyAndAddAllIfNeeded(this.props.selection, selection);
        return () => this.props.onSelectionChange?.(newSelection);
    };

    handleSelectionUpdate = (tv: string, selected: boolean) => {
        const newSelection = selected
            ? Utils.copyAndAddIfNeeded(this.props.selection, tv)
            : Utils.copyAndRemoveIfNeeded(this.props.selection, tv);
        this.props.onSelectionChange(newSelection);
    };

    handleClearSelection = () => {
        this.props.onSelectionChange?.([]);
    };

    handleNewHotspotClick = () => {
        ShowModalEvent.fireNotClosable(<HotspotPanel tv={undefined} />, HotspotPanel.HOTSPOT_PANEL_WIDTH_EM);
    };

    switchDisplayMode = () => this.setState({ gridDisplay: !this.state.gridDisplay });
}
